import { AutocompleteWithLabel, SelectWithLabel } from '@adotel/components'
import { useLazyQuery, useQuery } from '@apollo/client'
import { Button, MenuItem, Typography } from '@material-ui/core'
import ArrowForwardIcon from '@material-ui/icons/ArrowForward'
import { FormikErrors, FormikTouched } from 'formik'
import { useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { toast } from 'react-toastify'

import SectionTitle from '../../components/SectionTitle'
import { ACCOMMODATIONS_QUERY } from '../../graphql/accommodation'
import { ENTITY_LIST_QUERY } from '../../graphql/entityList'
import { ROLES_QUERY } from '../../graphql/role'
import { TENANTS_QUERY } from '../../graphql/tenant'
import { getTranslate, lang } from '../../localization'
import { Accommodation } from '../../types/accommodation'
import { EntityList } from '../../types/entityList'
import { EntityType } from '../../types/reserveMonitoringList'
import { RoleBrief } from '../../types/role'
import { Tenant } from '../../types/tenants'
import { Resource, UserForCreateAndModify } from '../../types/users'
import useStyles from './styles/userRole.styles'

interface UserRoleProps {
  values: UserForCreateAndModify
  setFieldValue: (
    field: string,
    value: any,
    shouldValidate?: boolean | undefined
  ) => void
  errors: FormikErrors<UserForCreateAndModify>
  touched: FormikTouched<UserForCreateAndModify>
  handleBlur: {
    (e: React.FocusEvent<any, Element>): void
    <T = any>(fieldOrEvent: T): T extends string ? (e: any) => void : void
  }
  isSubmitting: boolean
  params: {
    id: string
  }
}

const UserRole: React.FC<UserRoleProps> = ({
  values,
  setFieldValue,
  errors,
  touched,
  handleBlur,
  isSubmitting,
  params
}) => {
  const classes = useStyles()
  const history = useHistory()
  const [autoCompleteForRole, setAutoCompleteForRele] = useState<any>(null)
  const [autoCompleteForEntity, setAutoCompleteForEntity] = useState<any>(null)
  const [autoCompleteForResources, setAutoCompleteForResources] = useState<
    any[]
  >([])
  const [resourcesOptions, setResourcesOptions] = useState<any[]>([])

  const [getTenants, { loading: loadingForTenant }] = useLazyQuery<{
    tenants: Tenant[]
  }>(TENANTS_QUERY, {
    notifyOnNetworkStatusChange: true,
    onCompleted: (data) => {
      setResourcesOptions(
        data.tenants.map((tenantItem) => ({
          label: tenantItem.basicDetails.name,
          value: tenantItem.basicDetails.tenantId,
          code: tenantItem.basicDetails.displayTenantCode
        }))
      )
    }
  })

  const [
    getAccommodations,
    { data: accommodations, loading: loadingForAccommodations }
  ] = useLazyQuery<{
    accommodations: Accommodation[]
  }>(ACCOMMODATIONS_QUERY, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      if (values.selectedResourceType.label === 'ACCOMMODATION') {
        setResourcesOptions(
          data.accommodations.map((accItem) => ({
            label: accItem.basicDetails.name,
            value: accItem.basicDetails.displayPropertyCode || '',
            code: accItem.basicDetails.displayPropertyCode,
            tenantName: accItem.basicDetails.tenantName
          }))
        )
      }
    }
  })

  useEffect(() => {
    if (autoCompleteForRole && autoCompleteForRole.code === 'Channel') {
      getAccommodations()
      if (values.selectedResourceType.label === 'Tenant') {
        getTenants()
      }
    }
  }, [values.selectedResourceType.label, autoCompleteForRole])

  const { data: rolesData } = useQuery<{
    roles: RoleBrief[]
  }>(ROLES_QUERY, {
    variables: { filters: {} },
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
    skip: !!params.id && !values.displayRoleID,
    onCompleted: (res) => {
      const role = res?.roles.find(
        (r) => r.displayRoleID == values?.displayRoleID
      )

      setAutoCompleteForRele({
        label: role?.name,
        code: role?.entityType,
        value: role?.roleID
      })
    }
  })

  const [getEntityList, { data: entityListData }] = useLazyQuery<{
    entityList: EntityList[]
  }>(ENTITY_LIST_QUERY, {
    onError: (err) => {
      toast.error(err.message)
    }
  })

  useEffect(() => {
    if (autoCompleteForRole && autoCompleteForRole.code) {
      getEntityList({
        variables: {
          entityType: autoCompleteForRole.code
        }
      })

      setAutoCompleteForEntity({
        label: values.entityName,
        value: values.entityType
      })
    }
  }, [autoCompleteForRole])

  useEffect(() => {
    setAutoCompleteForResources(
      values.resources.map((item: Resource) => ({
        label: item.entityName,
        value: item.displayEntityCode,
        code: item.displayEntityCode
      }))
    )
  }, [resourcesOptions])

  return (
    <div>
      <div className={classes.header}>
        <SectionTitle title={getTranslate('Role')} marginBottom />
        {params.id && (
          <Button
            variant="contained"
            color="primary"
            endIcon={<ArrowForwardIcon />}
            className={classes.btn}
            onClick={() => {
              history.push(
                `/${lang}/access-management/user-management/user/${params.id}/permissions`
              )
            }}>
            <Typography className={classes.whiteText}>
              {getTranslate('User Permissions')}
            </Typography>
          </Button>
        )}
      </div>
      <div className={classes.row}>
        <AutocompleteWithLabel
          options={
            rolesData?.roles && rolesData?.roles.length > 0
              ? rolesData?.roles.map((role) => ({
                  label: role.name,
                  value: role.roleID,
                  code: (role.entityType as EntityType) || ''
                }))
              : []
          }
          inputSize="small"
          getOptionLabel={(option) => option.label || ''}
          getOptionSelected={(option, value) =>
            option.value === value.value && option.label === value.label
          }
          value={autoCompleteForRole}
          onChange={(_, value: any) => {
            if (value) {
              setAutoCompleteForRele({
                value: value.value,
                code: value.code,
                label: value.label
              })
              setFieldValue('displayRoleID', value.label)

              setFieldValue('roleID', value.value)
              if (value.code) {
                setFieldValue('entityType', value.code)
              } else {
                setFieldValue('entityType', undefined)
              }
              setFieldValue('entityID', undefined)
              setFieldValue('entityName', undefined)
              setFieldValue('entityCode', undefined)
              setAutoCompleteForEntity(null)
              setFieldValue('selectedResourceType.label', '')
              setFieldValue('resources', [])
            } else {
              setAutoCompleteForRele(null)
              setAutoCompleteForEntity(null)
              setAutoCompleteForResources([])
              setFieldValue('displayRoleID', '')
              setFieldValue('entityID', undefined)
              setFieldValue('entityName', undefined)
              setFieldValue('entityCode', undefined)
              setFieldValue('selectedResourceType.label', '')
              setFieldValue('resources', [])
            }
          }}
          error={(touched.displayRoleID && !!errors.displayRoleID) || undefined}
          label={getTranslate('Role')}
        />
        {autoCompleteForRole && autoCompleteForRole.code && (
          <AutocompleteWithLabel
            options={
              entityListData?.entityList
                ? entityListData?.entityList.map((entity) => ({
                    label: entity.label,
                    value: entity.entityCode,
                    id: entity.id
                  }))
                : []
            }
            inputSize="small"
            getOptionLabel={(option) => option.label || ''}
            value={autoCompleteForEntity}
            onChange={(_, value: any) => {
              if (value) {
                setAutoCompleteForEntity({
                  value: value.value,
                  label: value.label
                })
                setFieldValue('entityID', value.id)
                setFieldValue('entityCode', value.value)
                setFieldValue('selectedResourceType.label', '')
                setFieldValue('resources', [])
                setAutoCompleteForResources([])
              } else {
                setAutoCompleteForEntity(null)
                setFieldValue('entityID', undefined)
                setFieldValue('entityCode', undefined)
                setFieldValue('selectedResourceType.label', '')
                setFieldValue('resources', [])
                setAutoCompleteForResources([])
              }
            }}
            label={getTranslate('Entity')}
          />
        )}
        {autoCompleteForRole &&
          autoCompleteForRole.code === 'Channel' &&
          autoCompleteForEntity && (
            <SelectWithLabel
              name="selectedResourceType.label"
              fullWidth
              variant="outlined"
              size="small"
              label={getTranslate('Resource Type')}
              value={values.selectedResourceType.label}
              onChange={(event) => {
                setFieldValue('selectedResourceType.label', event.target.value)
                setFieldValue('resources', [])
                setAutoCompleteForResources([])
              }}
              error={
                (touched.selectedResourceType?.label &&
                  !!errors.selectedResourceType?.label) ||
                undefined
              }
              helperText={
                touched.selectedResourceType?.label
                  ? getTranslate(errors.selectedResourceType?.label || '')
                  : undefined
              }
              onBlur={handleBlur}
              disabled={isSubmitting || !autoCompleteForEntity}>
              <MenuItem key="Tenant" value="Tenant">
                {getTranslate('Tenant')}
              </MenuItem>
              <MenuItem key="Accommodation" value="ACCOMMODATION">
                {getTranslate('Accommodation')}
              </MenuItem>
            </SelectWithLabel>
          )}

        {values.selectedResourceType.label !== '' &&
          autoCompleteForRole &&
          autoCompleteForRole.code === 'Channel' &&
          autoCompleteForEntity && (
            <AutocompleteWithLabel
              loading={loadingForTenant || loadingForAccommodations}
              loadingText={getTranslate('Loading') + '...'}
              options={values ? resourcesOptions : []}
              size="small"
              getOptionLabel={(option) => option.label || ''}
              getOptionSelected={(option, value) => option.code === value.code}
              label={getTranslate('Resources')}
              limitTags={1}
              showSelectAll
              value={autoCompleteForResources}
              inputSize="small"
              onChange={(event, v: any) => {
                const selectedResources: Resource[] = []

                v.forEach((value: any) => {
                  if (values.selectedResourceType.label === 'ACCOMMODATION') {
                    selectedResources.push({
                      entityId: value.value,
                      entityCode: value.entityCode,
                      displayEntityCode: value.displayEntityCode,
                      entityName: value.label,
                      entityType: 'Accommodation',
                      TenantName: value.tenantName,
                      AccommodationName: value.label,
                      ValidFrom: '',
                      ValidUntil: ''
                    })
                    setFieldValue('resources', selectedResources)
                  } else {
                    accommodations?.accommodations
                      .filter((ac) => ac.basicDetails.tenantId === value.value)
                      .forEach((acc) => {
                        if (acc.basicDetails.accommodationID)
                          selectedResources.push({
                            entityId: acc.basicDetails.accommodationID,
                            entityCode: acc.basicDetails.propertyCode,
                            displayEntityCode:
                              acc.basicDetails.displayPropertyCode || '',
                            entityName: value.label,
                            entityType: 'Accommodation',
                            TenantName: value.label,
                            AccommodationName:
                              lang === 'en'
                                ? acc.basicDetails.name
                                : acc.basicDetails.nameTranslatedList[0].value,
                            ValidFrom: '',
                            ValidUntil: ''
                          })
                      })
                    setFieldValue('resources', selectedResources)
                  }
                })

                if (v.length > 0) {
                  setAutoCompleteForResources(
                    v.map((value: any) => ({
                      value: value.value,
                      label: value.label,
                      code: value.code
                    }))
                  )
                } else {
                  setAutoCompleteForResources([])
                  setFieldValue('resources', [])
                }
              }}
              multiple
              onBlur={handleBlur}
              disabled={isSubmitting}
            />
          )}
      </div>
    </div>
  )
}
export default UserRole
